import React, { useMemo } from 'react';
import { Box, Card, Skeleton, Typography } from '@mui/material';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip as ReTooltip } from 'recharts';
import { EmptyContent } from 'src/components/empty-content';
import { formatInt } from 'src/utils/format';
import { useTranslate } from 'react-admin';

export type PlatformDonutItem = { name: string; value: number; views?: number; users?: number; viewsPerUser?: number };
export type PlatformTotals = Record<string, number>;

const DONUT_COLORS = ['#60A5FA', '#605E68', '#E76E50', '#E2E8F0'] as const;

function PlatformTooltipContent({ active, payload, t }: any) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload as PlatformDonutItem;
  if (!row) return null;

  return (
    <Box sx={{ bgcolor: 'rgba(0,0,0,0.61)', color: '#fff', borderRadius: 2, px: 2, py: 1.5, minWidth: 180 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>{row.name}</Typography>

      <Line label={t('module.dashboardV2.platform.tooltip.pctOfUsers', { _: '% of Users' })} value={`${row.value}%`} />
      <Line label={t('module.dashboardV2.platform.tooltip.users', { _: 'Users' })} value={formatInt(row.users ?? 0)} />
      <Line label={t('module.dashboardV2.platform.tooltip.views', { _: 'Views' })} value={formatInt(row.views ?? 0)} />
      <Line
        label={t('module.dashboardV2.platform.tooltip.viewsPerUser', { _: 'Views / User' })}
        value={(row.viewsPerUser ?? 0).toFixed(2)}
      />
    </Box>
  );
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.4 }}>
      <Typography sx={{ fontSize: 12, opacity: 0.92 }}>{label}</Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 500 }}>{value}</Typography>
    </Box>
  );
}

function PlatformLegend({ items }: { items: PlatformDonutItem[] }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        ml: 2,
      }}
    >
      {items.map((it, idx) => (
        <Box
          key={it.name}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: 0.5,
              bgcolor: DONUT_COLORS[idx % DONUT_COLORS.length],
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 400,
              color: 'text.secondary',
              whiteSpace: 'nowrap',
            }}
          >
            {it.name}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export function PlatformDonutCard(props: {
  title: string;
  items: PlatformDonutItem[];
  totals?: PlatformTotals;
  loading?: boolean;
}) {
  const { items, totals, loading } = props;
  const t = useTranslate();

  const topItems = useMemo(() => items.slice(0, 4), [items]);
  const hasData = (topItems?.length ?? 0) > 0;

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          bgcolor: 'common.white',
        }}
      >
        {topItems.map((it, idx) => (
          <Box
            key={it.name}
            sx={{
              px: 2.5,
              py: 2,
              ...(idx !== 0 && {
                borderLeft: '1px solid',
                borderColor: 'grey.200',
              }),
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 400,
                color: 'text.secondary',
              }}
            >
              {it.name}
            </Typography>

            {loading ? (
              <Skeleton variant="text" height={28} sx={{ mt: 0.5 }} />
            ) : (
              <Typography
                sx={{
                  fontSize: 24,
                  fontWeight: 600,
                  lineHeight: 1.2,
                  mt: 0.5,
                  color: 'text.primary',
                }}
              >
                {totals?.[it.name] ?? 0}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      {/* Donut */}
      <Box
        sx={{
          height: 300,
          px: 2,
          py: 2,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {loading ? (
          <Skeleton variant="rounded" sx={{ width: '100%', height: 300 }} />
        ) : !hasData ? (
          <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '40px' }}>
            <EmptyContent title={t('common.noData', { _: 'No data' })} />
          </Box>
        ) : topItems.length ? (
          <>
            <Box sx={{ flex: 1, height: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={topItems}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={70}
                    outerRadius={110}
                    paddingAngle={0}
                    stroke="none"
                  >
                    {topItems.map((_, idx) => (
                      <Cell key={idx} fill={DONUT_COLORS[idx % DONUT_COLORS.length]} />
                    ))}
                  </Pie>

                  <ReTooltip
                    content={(props) => <PlatformTooltipContent {...props} t={t} />}
                    cursor={false}
                    wrapperStyle={{ outline: 'none' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>

            <PlatformLegend items={topItems} />
          </>
        ) : (
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {t('common.noData', { _: 'No data' })}
          </Typography>
        )}
      </Box>
    </Card>
  );
}